"use client";

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export interface TabItem {
  key: string;
  label: ReactNode;
  content: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  defaultActiveKey?: string;
  activeKey?: string;
  onChange?: (key: string) => void;
}

/**
 * K2（B3 瀏覽器稽核，中）：原本沒有鍵盤支援——`role=tablist` 底下每個 tab
 * 都是 `tabIndex=0`（非 roving），左右鍵/Home/End 無效、沒有 `aria-controls`，
 * tabpanel 也沒有 `aria-labelledby`。對照 WAI-ARIA APG Tabs Pattern 補齊，
 * 前台 `web/src/components/courses/Tabs.tsx` 與
 * `admin/src/components/cms/LocaleTabs.tsx` 都已是同一套做法（後者可參考，
 * 但那两個檔不在本套件領域內，不能直接共用程式碼，這裡照同樣的模式重寫）。
 */
export function Tabs({ items, defaultActiveKey, activeKey, onChange }: TabsProps) {
  useInjectStyles();
  const uid = useId();
  const [internal, setInternal] = useState(defaultActiveKey ?? items[0]?.key);
  const active = activeKey ?? internal;
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const select = (key: string) => {
    if (activeKey === undefined) setInternal(key);
    onChange?.(key);
  };

  const activeIndex = items.findIndex((i) => i.key === active);
  const activeItem = items.find((i) => i.key === active);

  const focusAndSelect = (index: number) => {
    const item = items[index];
    if (!item) return;
    select(item.key);
    tabRefs.current[item.key]?.focus();
  };

  /*
   * roving tabindex：tablist 只佔一個 Tab 停留點，組內用左右鍵移動、Home/End
   * 跳頭尾——宣告了 role=tablist 就帶著這個義務，不然是個沒兌現的承諾。
   */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (activeIndex < 0) return;
    let nextIndex: number | null = null;
    if (e.key === "ArrowRight") nextIndex = (activeIndex + 1) % items.length;
    else if (e.key === "ArrowLeft") nextIndex = (activeIndex - 1 + items.length) % items.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = items.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    focusAndSelect(nextIndex);
  };

  return (
    <div className="cui-reset">
      <div className="cui-tabs-list" role="tablist" onKeyDown={onKeyDown}>
        {items.map((item) => {
          const selected = item.key === active;
          const tabId = `cui-tab-${uid}-${item.key}`;
          const panelId = `cui-tabpanel-${uid}-${item.key}`;
          return (
            <button
              key={item.key}
              ref={(el) => {
                tabRefs.current[item.key] = el;
              }}
              id={tabId}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              className="cui-tab"
              onClick={() => select(item.key)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {activeItem && (
        <div
          className="cui-tab-panel"
          role="tabpanel"
          id={`cui-tabpanel-${uid}-${activeItem.key}`}
          aria-labelledby={`cui-tab-${uid}-${activeItem.key}`}
        >
          {activeItem.content}
        </div>
      )}
    </div>
  );
}
